import { IApiResponse, IBaseData } from '@datatype/common/api-type';

// ###################################
// common
// ###################################
export enum EApiLanguageCode {
  ENGLISH = 'EN',
  JAPANESE = 'JA',
}

export enum EApiStudyType {
  CHARACTER = 'CHARACTER',
  CLASSROOM_GRAMMAR = 'CLASSROOM_GRAMMAR',
  CLASSROOM_EXPRESSION = 'CLASSROOM_EXPRESSION',
  CLASSROOM_CONVERSATION = 'CLASSROOM_CONVERSATION',
  AUTHENTIC_EXPRESSION = 'AUTHENTIC_EXPRESSION',
  SPEAKING = 'SPEAKING',
  WORKSHEET = 'WORKSHEET',
  MINI_STORY = 'MINI_STORY',
  VOCABULARY_BOOK = 'VOCABULARY_BOOK',
  VOCABULARY_CLOZE = 'VOCABULARY_CLOZE',
  GRAMMAR_CLOZE = 'GRAMMAR_CLOZE',
}

// ############### study plan (첫 학습 전 학습 계획) ###############
// 유입 경로 — 광고 어트리뷰션 대용 (Meta SDK 미연동 상태 보완)
//
// SNS(인스타/페북/유튜브)를 한 항목으로 묶은 이유:
// - 지금 돈을 쓰는 광고는 Meta(인스타)와 구글 UAC 둘. 유튜브 광고는 따로 돌리지 않음
// - 유튜브에 뜨는 건 구글 UAC가 자동 배포한 것이고, UAC 성과는 구글 애즈 콘솔에서 직접 확인 가능
// - 즉 이 설문으로 추적해야 할 유일한 채널은 Meta. 유튜브를 쪼개도 얻는 정보가 없음
// - 유튜브 광고를 별도 집행하게 되면 그때 항목을 분리할 것
//
// STORE(스토어 검색 = ASO)와 SEARCH(웹 검색 = SEO)는 개선 액션이 달라 분리.
export enum EApiSourceChannel {
  SNS = 'SNS',
  // 앱스토어/플레이스토어 공통 (특정 브랜드에 묶이지 않게 STORE)
  STORE = 'STORE',
  SEARCH = 'SEARCH',
  // 블로그 · 커뮤니티 글 (찾아가 읽는 유입 — 피드에 밀어주는 SNS와 구분)
  ARTICLE = 'ARTICLE',
  // 지인 추천·소개로 유입 (마케팅 표준 용어. 친구뿐 아니라 지인·선생님 등 사람을 통한 유입 전반)
  REFERRAL = 'REFERRAL',
  ETC = 'ETC',
}

// 학습 동기 — 복수 선택
//
// 서로 겹치지 않는 4개 축 + 기타로 구성:
// - HOBBY(콘텐츠를 즐긴다) / CONVERSATION(사람과 말한다) / STUDY(학업 — 시험·유학) / WORK(일에 쓴다)
// - TRAVEL은 CONVERSATION의 하위 상황이라 흡수 (여행 가려는 사람도 결국 '말하고 싶은' 것)
// - '자기계발'은 별도 축이 아니라 상위 개념 (학업도 업무도 자기계발) → ETC로 흡수
export enum EApiStudyMotivation {
  HOBBY = 'HOBBY',
  CONVERSATION = 'CONVERSATION',
  STUDY = 'STUDY',
  WORK = 'WORK',
  ETC = 'ETC',
}

// 사용자가 직접 선택하는 언어 실력 5단계 (입문/기초/초급/중급/고급)
export enum EApiLanguageLevel {
  BEGINNER = 'BEGINNER',
  BASIC = 'BASIC',
  ELEMENTARY = 'ELEMENTARY',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
}

// 하루 학습량 목표 — UI에서는 각 단계에 대응하는 예상 학습 시간을 안내한다.
export enum EApiStudyTimeGoal {
  MINIMAL = 'MINIMAL',
  MEDIUM = 'MEDIUM',
  CONSIDERABLE = 'CONSIDERABLE',
  MAXIMAL = 'MAXIMAL',
}

export enum EApiClozeLevel {
  EASY = 'EASY',
  NORMAL = 'NORMAL',
  HARD = 'HARD',
}

export enum EApiTest {
  TOEIC950 = 'TOEIC950',
  TOEIC850 = 'TOEIC850',
  TOEIC750 = 'TOEIC750',
  TOEIC650 = 'TOEIC650',
  TOEIC550 = 'TOEIC550',
  JLPT1 = 'JLPT1',
  JLPT2 = 'JLPT2',
  JLPT3 = 'JLPT3',
  JLPT4 = 'JLPT4',
  JLPT5 = 'JLPT5',
}

export type TClozeType = 'VOCABULARY' | 'GRAMMAR' | 'LISTENING';

export enum EApiAttendanceType {
  ENGLISH_CLOZE_VOCABULARY = 'ENGLISH_CLOZE_VOCABULARY',
  ENGLISH_CLOZE_GRAMMAR = 'ENGLISH_CLOZE_GRAMMAR',
  ENGLISH_CLOZE_LISTENING = 'ENGLISH_CLOZE_LISTENING',
  JAPANESE_CLOZE_VOCABULARY = 'JAPANESE_CLOZE_VOCABULARY',
  JAPANESE_CLOZE_GRAMMAR = 'JAPANESE_CLOZE_GRAMMAR',
  JAPANESE_CLOZE_LISTENING = 'JAPANESE_CLOZE_LISTENING',
  // [Deprecated] CLOZE_ 접두사 이전 값. 기존 attendance row 조회용으로만 남긴다.
  ENGLISH_VOCABULARY = 'ENGLISH_VOCABULARY',
  ENGLISH_GRAMMAR = 'ENGLISH_GRAMMAR',
  JAPANESE_VOCABULARY = 'JAPANESE_VOCABULARY',
  JAPANESE_GRAMMAR = 'JAPANESE_GRAMMAR',
  ENGLISH_AUTHENTIC_EXPRESSION = 'ENGLISH_AUTHENTIC_EXPRESSION',
  JAPANESE_AUTHENTIC_EXPRESSION = 'JAPANESE_AUTHENTIC_EXPRESSION',
  ENGLISH_STORY = 'ENGLISH_STORY',
  JAPANESE_STORY = 'JAPANESE_STORY',
  ENGLISH_VOCABULARY_BOOK = 'ENGLISH_VOCABULARY_BOOK',
  JAPANESE_VOCABULARY_BOOK = 'JAPANESE_VOCABULARY_BOOK',
  ENGLISH_CLASSROOM_CHARACTER = 'ENGLISH_CLASSROOM_CHARACTER',
  JAPANESE_CLASSROOM_CHARACTER = 'JAPANESE_CLASSROOM_CHARACTER',
  ENGLISH_CLASSROOM_GRAMMAR = 'ENGLISH_CLASSROOM_GRAMMAR',
  JAPANESE_CLASSROOM_GRAMMAR = 'JAPANESE_CLASSROOM_GRAMMAR',
  ENGLISH_CLASSROOM_EXPRESSION = 'ENGLISH_CLASSROOM_EXPRESSION',
  JAPANESE_CLASSROOM_EXPRESSION = 'JAPANESE_CLASSROOM_EXPRESSION',
  ENGLISH_CLASSROOM_CONVERSATION = 'ENGLISH_CLASSROOM_CONVERSATION',
  JAPANESE_CLASSROOM_CONVERSATION = 'JAPANESE_CLASSROOM_CONVERSATION',
  ENGLISH_SPEAKING = 'ENGLISH_SPEAKING',
  JAPANESE_SPEAKING = 'JAPANESE_SPEAKING',
  // [Deprecated] 테이블 리네임 이전 값. 기존 attendance row 조회용으로만 남긴다.
  JAPANESE_CLASSROOM_PRACTICAL_CONVERSATION = 'JAPANESE_CLASSROOM_PRACTICAL_CONVERSATION',
  OPEN = 'OPEN',
}

export enum EApiAttendancePlatform {
  WEB = 'WEB',
  IOS = 'IOS',
  ANDROID = 'ANDROID',
}

export type TWorkbookType = 'CLOZE' | 'TEST';
export type TTestCode =
  | 'T1100'
  | 'T1200'
  | 'T1300'
  | 'T1400'
  | 'T1500'
  | 'T2100'
  | 'T2200'
  | 'T2300'
  | 'T2400'
  | 'T2500';

// ###################################
// problem
// ###################################
export interface IProblemData extends IBaseData {
  instruction: string;
  instructionAudioLink: string;
  instructionTranslation: string;
  passage: string;
  passageAudioLink: string;
  passageTranslation: string;
  questionA: string;
  questionAAudioLink: string;
  questionATranslation: string;
  questionB: string;
  questionBAudioLink: string;
  questionBTranslation: string;
  questionC: string;
  questionCAudioLink: string;
  questionCTranslation: string;
  questionD: string;
  questionDAudioLink: string;
  questionDTranslation: string;
  questionE: string;
  questionEAudioLink: string;
  questionETranslation: string;
  choiceA: string[];
  choiceAAudioLink: string;
  choiceB: string[];
  choiceBAudioLink: string;
  choiceC: string[];
  choiceCAudioLink: string;
  choiceD: string[];
  choiceDAudioLink: string;
  choiceE: string[];
  choiceEAudioLink: string;
  answerA: number;
  answerB: number;
  answerC: number;
  answerD: number;
  answerE: number;
  explanationA: string;
  explanationB: string;
  explanationC: string;
  explanationD: string;
  explanationE: string;
  vocabulary: string[];
  grammar: string[];
  hanja: string[];
  publishedAt: string;
  attemptCount: number;
  test: ITestData;
}

export interface IModifyProblemRequest {
  isAttempt?: boolean;
  problemKey: string;
}

// ###################################
// bundle
// ###################################
export interface IBundleData extends IBaseData {
  code: string;
  name: string;
  problems: IProblemData[];
}

// ###################################
// test
// ###################################
export interface ITestData extends IBaseData {
  code: string;
  order: number;
  name: string;
  description: string;
  subject: string;
  grade: string;
  category: string;
}

// ###################################
// student
// ###################################
export interface IStudentData extends IBaseData {
  signedup: boolean;
  studyConfiguration: IStudyConfigurationData;
}

export interface ICreateStudentRequest {
  studyLanguage: EApiLanguageCode;
  sourceChannel: EApiSourceChannel;
  studyMotivation: EApiStudyMotivation[];
  languageLevel: EApiLanguageLevel;
  studyTimeGoal: EApiStudyTimeGoal;
}

export interface IModifyStudentRequest {
  studentKey: string;
  testCodes?: string[];
  studyLanguage?: EApiLanguageCode;
  sourceChannel?: EApiSourceChannel;
  studyMotivation?: EApiStudyMotivation[];
  languageLevel?: EApiLanguageLevel;
  studyTimeGoal?: EApiStudyTimeGoal;
  englishVocabularyClozeLevel?: EApiClozeLevel;
  englishGrammarClozeLevel?: EApiClozeLevel;
  englishListeningClozeLevel?: EApiClozeLevel;
  englishVocabularyClozeGoal?: number;
  englishGrammarClozeGoal?: number;
  englishListeningClozeGoal?: number;
  japaneseVocabularyClozeLevel?: EApiClozeLevel;
  japaneseGrammarClozeLevel?: EApiClozeLevel;
  japaneseListeningClozeLevel?: EApiClozeLevel;
  japaneseVocabularyClozeGoal?: number;
  japaneseGrammarClozeGoal?: number;
  japaneseListeningClozeGoal?: number;
  toeicTestLevel?: EApiTest;
  toeic950TestGoal?: number;
  toeic850TestGoal?: number;
  toeic750TestGoal?: number;
  toeic650TestGoal?: number;
  toeic550TestGoal?: number;
  jlptTestLevel?: EApiTest;
  jlpt1TestGoal?: number;
  jlpt2TestGoal?: number;
  jlpt3TestGoal?: number;
  jlpt4TestGoal?: number;
  jlpt5TestGoal?: number;
  englishTestVocabularyBookLevel?: EApiVocabularyBookLevel;
  englishStudyVocabularyBookLevel?: EApiVocabularyBookLevel;
  japaneseTestVocabularyBookLevel?: EApiVocabularyBookLevel;
  japaneseStudyVocabularyBookLevel?: EApiVocabularyBookLevel;
}

export interface ICheckAttendanceRequest {
  studentKey: string;
  attendedAt: string;
  attendanceType: EApiAttendanceType;
}

export interface ICreateStudyRecordRequest {
  studentKey: string;
  studyType: EApiStudyType;
  languageCode: EApiLanguageCode;
  studyTime?: number;
  studyCount?: number;
}

export interface IAttendanceData extends IBaseData {
  attendedAt: string;
  attendanceType: EApiAttendanceType;
  platform: EApiAttendancePlatform;
}

export interface IReadAttendancePageRequest {
  studentKey: string;
  attendanceType: EApiAttendanceType;
  page: number;
  perPage: number;
}

// ###################################
// study configuration
// ###################################
export interface IStudyConfigurationData extends IBaseData {
  testCodes: string[];
  studyLanguage: EApiLanguageCode;
  sourceChannel: EApiSourceChannel | null;
  englishStudyMotivation: EApiStudyMotivation[] | null;
  englishLanguageLevel: EApiLanguageLevel | null;
  englishStudyTimeGoal: EApiStudyTimeGoal | null;
  japaneseStudyMotivation: EApiStudyMotivation[] | null;
  japaneseLanguageLevel: EApiLanguageLevel | null;
  japaneseStudyTimeGoal: EApiStudyTimeGoal | null;

  englishVocabularyClozeLevel: EApiClozeLevel;

  englishGrammarClozeLevel: EApiClozeLevel;

  englishListeningClozeLevel: EApiClozeLevel;

  englishVocabularyClozeGoal: number;

  englishGrammarClozeGoal: number;

  englishListeningClozeGoal: number;

  toeicTestLevel: EApiTest;

  toeic950TestGoal: number;

  toeic850TestGoal: number;

  toeic750TestGoal: number;

  toeic650TestGoal: number;

  toeic550TestGoal: number;

  japaneseVocabularyClozeLevel: EApiClozeLevel;

  japaneseGrammarClozeLevel: EApiClozeLevel;

  japaneseListeningClozeLevel: EApiClozeLevel;

  japaneseVocabularyClozeGoal: number;

  japaneseGrammarClozeGoal: number;

  japaneseListeningClozeGoal: number;

  jlptTestLevel: EApiTest;

  jlpt1TestGoal: number;

  jlpt2TestGoal: number;

  jlpt3TestGoal: number;

  jlpt4TestGoal: number;

  jlpt5TestGoal: number;

  englishTestVocabularyBookLevel: EApiVocabularyBookLevel;

  englishStudyVocabularyBookLevel: EApiVocabularyBookLevel;

  japaneseTestVocabularyBookLevel: EApiVocabularyBookLevel;

  japaneseStudyVocabularyBookLevel: EApiVocabularyBookLevel;
}

// ############### workbook ###############
export interface IStudyRecordData {
  studyDate: string;
  studyType: 'vocabulary' | 'grammar' | 'test';
  studyCount: number;
  studyTime: number;
}

// ###################################
// vocabulary
// ###################################
export interface IVocabularyData extends IBaseData {
  languageCode: string;
  vocabularyType: string;
  wordClass: string;
  notationType: string;
  keyword: string;
  subKeyword: string;
  meaning: string;
  description: string;
  pronunciation: string;
  level: string;
  example: string[];
  editedAt: number;
}

// ###################################
// cloze
// ###################################
export interface IClozeExplanation {
  language: string;
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  alternative: {
    keyword: string;
    meaning: string[];
    usage: string;
    pronunciation: string[];
    kanji:
      | {
          character: string;
          meaning: string[];
          onyomi: string[];
          kunyomi: string[];
        }[]
      | null;
    example: {
      sentence: string;
      translation: string;
    }[];
  }[];
  inflection: { type: string; form: string }[];
  kanji:
    | {
        character: string;
        meaning: string[];
        onyomi: string[];
        kunyomi: string[];
      }[]
    | null;
  example: {
    sentence: string;
    translation: string;
  }[];
}

export interface IClozeData extends IBaseData {
  clozeType: string;
  languageCode: string;
  level: string;
  sourceKeyword: string | null;
  sourceKeywordPronunciation: string | null;
  sourceKeywordAudioLink: string | null;
  keywordMeaningKo: string | null;
  keywordMeaningEn: string | null;
  keywordMeaningJa: string | null;
  sentenceKo: string;
  sentencePronunciationKo: string;
  sentenceTemplateKo: string;
  sentenceAudioLinkKo: string;
  sentenceEn: string;
  sentencePronunciationEn: string;
  sentenceTemplateEn: string;
  sentenceAudioLinkEn: string;
  sentenceJa: string;
  sentencePronunciationJa: string;
  sentenceTemplateJa: string;
  sentenceAudioLinkJa: string;
  choiceKo: string[];
  choicePronunciationKo: string[];
  choiceEn: string[];
  choicePronunciationEn: string[];
  choiceJa: string[];
  choicePronunciationJa: string[];
  answerKo: string[];
  answerPronunciationKo: string[];
  answerEn: string[];
  answerPronunciationEn: string[];
  answerJa: string[];
  answerPronunciationJa: string[];

  answerExplanationKo: IClozeExplanation | null;
  answerExplanationEn: IClozeExplanation | null;
  answerExplanationJa: IClozeExplanation | null;

  addExplanationKo: IClozeExplanation[] | null;
  addExplanationEn: IClozeExplanation[] | null;
  addExplanationJa: IClozeExplanation[] | null;

  sourceConversation: ISourceConversationData | null;

  conversationKo: IConversationData | null;
  conversationEn: IConversationData | null;
  conversationJa: IConversationData | null;

  conversationAudioLink: string[] | null;

  publishedAt: string;
  editedAt: string | null;

  reviewScore: number | null;
  reviewComment: string | null;
}

// ###################################
// source conversation
// ###################################
export interface ISourceConversationSpeaker {
  id: string;
  gender: string;
  name: string;
}

interface ISourceConversationKeyLine {
  index: number;
}

interface ISourceConversationLine {
  index: number;
  speaker: string;
  sentence: string;
  pronunciation: string;
}

interface ISourceConversationChoice {
  sentence: string;
  pronunciation: string;
  correct: boolean;
}

export interface ISourceConversationProblem {
  lineIndex: number;
  choice: ISourceConversationChoice[];
}

export interface ISourceConversationData {
  situation: string;
  speaker: ISourceConversationSpeaker[];
  keyLine: ISourceConversationKeyLine;
  line: ISourceConversationLine[];
  problem: ISourceConversationProblem[];
}

// ###################################
// conversation
// ###################################
export interface IConversationLine {
  index: number;
  translation: string;
  explanation: string;
}

interface IConversationChoice {
  translation: string;
  explanation: string;
}

interface IConversationProblem {
  lineIndex: number;
  choice: IConversationChoice[];
  explanation: string;
}

export interface IConversationVocabulary {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: {
    sentence: string;
    translation: string;
  }[];
  kanji:
    | {
        character: string;
        meaning: string[];
        onyomi: string[];
        kunyomi: string[];
      }[]
    | null;
}

export interface IConversationGrammar {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: {
    sentence: string;
    translation: string;
  }[];
  kanji:
    | {
        character: string;
        meaning: string[];
        onyomi: string[];
        kunyomi: string[];
      }[]
    | null;
}

export interface IConversationData {
  situation: string;
  line: IConversationLine[];
  problem: IConversationProblem[];
  vocabulary: IConversationVocabulary[] | null;
  grammar: IConversationGrammar[] | null;
}

// ############### workbook ###############
export interface IWorkbookData extends IBaseData {
  workbookType: TWorkbookType;

  // student: IStudentData;

  cloze: IClozeData;

  problem: IProblemData;

  reviewAt: string;

  publishedAt: string;

  todayStudiedAt?: string;

  lastStudiedAt?: string;

  bookmarkedAt?: string;

  isCorrect: boolean;

  isBookmark: boolean;

  isBookmarkRepeat: boolean;

  isIncorrectRepeat: boolean;

  isIncorrectNote: boolean;
}

export interface IReadTodayClozeWorkbookRequest {
  languageCode: EApiLanguageCode;
  clozeType: TClozeType;
  studentKey?: string;
  // workbookCount: number;
}

export interface IReadBookmarkClozeWorkbookRequest {
  languageCode: EApiLanguageCode;
  clozeType: TClozeType;
  isBookmarkRepeat?: boolean;
  page: number;
  perPage: number;
}

export interface IReadIncorrectNoteClozeWorkbookRequest {
  languageCode: EApiLanguageCode;
  clozeType: TClozeType;
  isIncorrectRepeat?: boolean;
  page: number;
  perPage: number;
}

export interface ICreateTodayTestWorkbookRequest {
  testCode: TTestCode;
  // workbookCount: number;
}

export interface IModifyWorkbookRequest {
  workbookKey: string;
  isCorrect?: boolean;
  isBookmark?: boolean;
  isBookmarkRepeat?: boolean;
  isIncorrectRepeat?: boolean;
  isIncorrectNote?: boolean;
  studyTime?: number;
  reviewAfter?: number;
}

// ###################################
// vocabulary book
// ###################################
export type TVocabularyBookCategory = 'TEST' | 'STUDY';

// 백엔드 EVocabularyBookLevel enum과 1:1. (JLPT/TOEIC = 시험, EASIEST~HARDEST = 일반학습 5단계)
export type TVocabularyBookLevel =
  | 'JLPT5'
  | 'JLPT4'
  | 'JLPT3'
  | 'JLPT2'
  | 'JLPT1'
  | 'TOEIC550'
  | 'TOEIC650'
  | 'TOEIC750'
  | 'TOEIC850'
  | 'TOEIC950'
  | 'EASIEST'
  | 'EASY'
  | 'NORMAL'
  | 'HARD'
  | 'HARDEST';

export const TEST_VOCABULARY_BOOK_LEVELS: TVocabularyBookLevel[] = ['JLPT5', 'JLPT4', 'JLPT3', 'JLPT2', 'JLPT1'];

export const STUDY_VOCABULARY_BOOK_LEVELS: TVocabularyBookLevel[] = ['EASIEST', 'EASY', 'NORMAL', 'HARD', 'HARDEST'];

export enum EVocabularyGrade {
  EASY = 'EASY',
  NORMAL = 'NORMAL',
  HARD = 'HARD',
}

export interface IReadVocabularyBooksRequest {
  page: number;
  perPage: number;
  languageCode: EApiLanguageCode;
  category?: TVocabularyBookCategory | null;
  levels?: TVocabularyBookLevel[] | null;
  studentKey?: string;
}

export interface IReadVocabularyBookProgressRequest {
  languageCode: EApiLanguageCode;
  category: EApiVocabularyBookCategory;
  level: EApiVocabularyBookLevel;
  studentKey?: string;
}

export interface IVocabularyBookItemProgress {
  book: IVocabularyBookMeta;
  studiedCount: number;
  hardCount: number;
  normalCount: number;
  easyCount: number;
}

export interface IVocabularyBookProgress {
  books: IVocabularyBookItemProgress[];
  currentVocabularyBookKey: string | null;
  reviewCount: number;
  reviewHardCount: number;
  reviewNormalCount: number;
  reviewEasyCount: number;
}

export interface IReadVocabularyWorkbookBookRequest {
  vocabularyBookKey: string;
  studentKey?: string;
}

export interface IReadVocabularyWorkbookBookmarkPageRequest {
  page: number;
  perPage: number;
  languageCode: EApiLanguageCode;
  isBookmarkRepeat?: boolean;
}

export interface IReadVocabularyWorkbookIncorrectNotePageRequest {
  page: number;
  perPage: number;
  languageCode: EApiLanguageCode;
  isIncorrectRepeat?: boolean;
}

export interface IReadReviewVocabularyWorkbookPageRequest {
  page: number;
  perPage: number;
  languageCode: EApiLanguageCode;
  category: EApiVocabularyBookCategory;
  level: EApiVocabularyBookLevel;
}

export interface IModifyVocabularyWorkbookRequest {
  // PATCH path key. cloze의 workbookKey와 동일 패턴. 책 진입 응답에서 받음
  workbookKey: string;
  isBookmark?: boolean | null;
  grade?: EVocabularyGrade | null;
  isBookmarkRepeat?: boolean | null;
  isIncorrectRepeat?: boolean | null;
  isIncorrectNote?: boolean | null;
  reviewAfter?: number;
}

// ###################################
// authentic expression
// ###################################
export type TContentType = 'DRAMA' | string;
export type TMediaType = 'VIDEO' | string;
export type TPlatform = 'YOUTUBE' | string;

export interface IAuthenticExpressionSourceData extends IBaseData {
  languageCode: string;
  contentType: TContentType;
  mediaType: TMediaType;
  platform: TPlatform;
  channel: string;
  contentId: string;
  title: string;
  link: string;
  clipStart: number;
  clipEnd: number;
  imageLink: string | null;
  audioLink: string | null;
  videoLink: string | null;
  downloadedAt: string;
  consumedAt: string | null;
  expression: IAuthenticExpressionData | null;
}

export interface ISourceSegment {
  index: number;
  start: number;
  end: number;
  text: string;
  speaker: string | null;
  pronunciation: string | null;
  substantive: boolean;
}

export interface ISourceKeySegment {
  index: number;
  sentence: string;
  keyExpression: string;
  keyExpressionTemplate: string;
  keyExpressionAnswer: string[];
  keyExpressionPronunciation: string;
}

export interface IKeySegment {
  index: number;
  sentence: string;
  keyExpression: string;
  keyExpressionTranslation: string;
  keyExpressionContext: string;
  keyExpressionExplanation: string;
  keyExpressionExample: {
    sentence: string;
    translation: string;
  }[];
}

export interface ISegment {
  index: number;
  sentence: string;
  translation: string;
  explanation: string;
}

export interface IExpressionNote {
  vocabulary: IExpressionVocabulary[] | null;
  grammar: IExpressionGrammar[] | null;
}

export interface IExpressionVocabulary {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: {
    sentence: string;
    translation: string;
  }[];
  kanji:
    | {
        character: string;
        meaning: string[];
        onyomi: string[];
        kunyomi: string[];
      }[]
    | null;
}

export interface IExpressionGrammar {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: {
    sentence: string;
    translation: string;
  }[];
  kanji:
    | {
        character: string;
        meaning: string[];
        onyomi: string[];
        kunyomi: string[];
      }[]
    | null;
}

// ###################################
// story
// ###################################
export enum EApiStoryType {
  MINI_STORY = 'MINI_STORY',
}

export interface IStorySpeaker {
  id: string;
  gender: string;
  name: string;
}

export interface IStoryKeyLine {
  index: number;
}

export interface IStorySourceLine {
  index: number;
  speaker: string;
  sentence: string;
  pronunciation: string;
}

export interface IStorySourceChoice {
  sentence: string;
  pronunciation: string;
  correct: boolean;
}

export interface IStorySourceProblem {
  lineIndex: number;
  choice: IStorySourceChoice[];
}

export interface IStoryLine {
  index: number;
  translation: string;
  explanation: string;
}

export interface IStoryChoice {
  translation: string;
  explanation: string;
}

export interface IStoryProblem {
  lineIndex: number;
  choice: IStoryChoice[];
  explanation: string;
}

export interface IStoryExample {
  sentence: string;
  translation: string;
}

export interface IStoryKanji {
  character: string;
  meaning: string[];
  onyomi: string[];
  kunyomi: string[];
}

export interface IStoryVocabulary {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: IStoryExample[];
  kanji: IStoryKanji[] | null;
}

export interface IStoryGrammar {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: IStoryExample[];
  kanji: IStoryKanji[] | null;
}

export interface IStoryNote {
  vocabulary: IStoryVocabulary[] | null;
  grammar: IStoryGrammar[] | null;
}

export interface IStoryData extends IBaseData {
  storyType: EApiStoryType;
  languageCode: EApiLanguageCode;
  keyword: string;

  sourceSpeaker: IStorySpeaker[];
  sourceKeyLine: IStoryKeyLine | null;
  sourceLine: IStorySourceLine[];
  sourceProblem: IStorySourceProblem[];

  situationKo: string;
  keyLineKo: IStoryKeyLine | null;
  lineKo: IStoryLine[];
  problemKo: IStoryProblem[];
  storyNoteKo: IStoryNote | null;

  situationEn: string;
  keyLineEn: IStoryKeyLine | null;
  lineEn: IStoryLine[];
  problemEn: IStoryProblem[];
  storyNoteEn: IStoryNote | null;

  situationJa: string;
  keyLineJa: IStoryKeyLine | null;
  lineJa: IStoryLine[];
  problemJa: IStoryProblem[];
  storyNoteJa: IStoryNote | null;

  sourceLineAudioLink: string[];

  reviewScore: number | null;
  reviewComment: string | null;
  publishedAt: string | null;
  releaseDate: string | null;
}

export interface IAuthenticExpressionData extends IBaseData {
  languageCode: string;
  authenticExpressionSource: IAuthenticExpressionSourceData;
  sourceSegment: ISourceSegment[];
  sourceKeySegment: ISourceKeySegment | null;
  keySegmentKo: IKeySegment | null;
  segmentKo: ISegment[] | null;
  keyExpressionNoteKo: IExpressionNote | null;
  expressionNoteKo: IExpressionNote | null;
  keySegmentEn: IKeySegment | null;
  segmentEn: ISegment[] | null;
  keyExpressionNoteEn: IExpressionNote | null;
  expressionNoteEn: IExpressionNote | null;
  keySegmentJa: IKeySegment | null;
  segmentJa: ISegment[] | null;
  keyExpressionNoteJa: IExpressionNote | null;
  expressionNoteJa: IExpressionNote | null;
  transcriptionReviewScore: number | null;
  transcriptionReviewComment: string | null;
  keyExpressionReviewScore: number | null;
  keyExpressionReviewComment: string | null;
  checkedAt: string | null;
  publishedAt: string | null;
  releaseDate: string | null;
}

// ###################################
// vocabulary-book domain (백엔드 응답 구조)
// ###################################

// 백엔드 EVocabularyBookCategory enum과 1:1 매핑
export enum EApiVocabularyBookCategory {
  TEST = 'TEST',
  STUDY = 'STUDY',
}

// 백엔드 EVocabularyBookLevel enum과 1:1 매핑
// - 시험준비(TEST, JA): JLPT5~JLPT1
// - 시험준비(TEST, EN): TOEIC550~TOEIC950
// - 일반학습(STUDY): EASIEST/EASY/NORMAL/HARD/HARDEST
export enum EApiVocabularyBookLevel {
  JLPT5 = 'JLPT5',
  JLPT4 = 'JLPT4',
  JLPT3 = 'JLPT3',
  JLPT2 = 'JLPT2',
  JLPT1 = 'JLPT1',
  TOEIC550 = 'TOEIC550',
  TOEIC650 = 'TOEIC650',
  TOEIC750 = 'TOEIC750',
  TOEIC850 = 'TOEIC850',
  TOEIC950 = 'TOEIC950',
  EASIEST = 'EASIEST',
  EASY = 'EASY',
  NORMAL = 'NORMAL',
  HARD = 'HARD',
  HARDEST = 'HARDEST',
}

// 백엔드 AiVocabularyOutput
export interface IAiVocabularyExample {
  sentence: string;
  translation: string;
}

export interface IAiVocabularyKanji {
  character: string;
  meaning: string[];
  onyomi: string[];
  kunyomi: string[];
}

export interface IAiVocabulary {
  key: string;
  source: string;
  sourcePronunciation: string[];
  sourceAudioLink: string[];
  wordClass: string | null;
  // 한국어 해설
  meaningKo: string[];
  usageKo: string | null;
  exampleKo: IAiVocabularyExample[];
  kanjiKo: IAiVocabularyKanji[];
  // 영어 해설
  meaningEn: string[];
  usageEn: string | null;
  exampleEn: IAiVocabularyExample[];
  kanjiEn: IAiVocabularyKanji[];
  // 일본어 해설
  meaningJa: string[];
  usageJa: string | null;
  exampleJa: IAiVocabularyExample[];
  kanjiJa: IAiVocabularyKanji[];
}

// 학습 기록이 단어(ai_vocabulary)에 물리도록 재설계 → workbook 응답이 bundle이 아니라 aiVocabulary를 직접 임베드.
// (구 IVocabularyBookBundle 임베드 폐기 — 편성 정보는 이제 workbook 응답에 포함 안 됨)
// export interface IVocabularyBookBundle {
//   key: string;
//   order: number;
//   aiVocabulary: IAiVocabulary;
// }

// 백엔드 AiVocabularyWorkbookResponse — 응답 단위 (학습 기록, 단어에 물림)
export interface IVocabularyWorkbookData {
  key: string;
  isBookmark: boolean | null;
  bookmarkedAt: string | null;
  grade: EVocabularyGrade | null;
  studiedAt: string | null;
  isIncorrectNote: boolean | null;
  isBookmarkRepeat: boolean | null;
  isIncorrectRepeat: boolean | null;
  aiVocabulary: IAiVocabulary;
}

export interface IVocabularyBook {
  key: string;
  languageCode: EApiLanguageCode;
  category: EApiVocabularyBookCategory;
  level: EApiVocabularyBookLevel;
  // 동일 (언어+카테고리+레벨) 내 순번 (1탄, 2탄...)
  order: number;
  name: string | null;
  description: string | null;
  // 한 권의 단어 수
  itemCount: number;
  isPlan: boolean;
  publishedAt: string | null;
}

// 책 메타 only (bundle 제외). progress 응답용
export interface IVocabularyBookMeta {
  key: string;
  languageCode: EApiLanguageCode;
  category: EApiVocabularyBookCategory;
  level: EApiVocabularyBookLevel;
  order: number;
  name: string | null;
  description: string | null;
  itemCount: number;
  isPlan: boolean;
  publishedAt: string | null;
}

// ###################################
// character (일본어교실 입문 — 가나)
// ###################################
export enum EApiCharacterType {
  HIRAGANA = 'HIRAGANA',
  KATAKANA = 'KATAKANA',
}

export enum EApiSubCharacterType {
  SEION = 'SEION', // 청음
  DAKUON = 'DAKUON', // 탁음
  HANDAKUON = 'HANDAKUON', // 반탁음
  SOKUON = 'SOKUON', // 촉음 (작게 쓰는 っ — 단독 발음 없음: UI에서 발음 버튼 숨김·퀴즈 제외)
  YOON = 'YOON', // 요음 (작게 쓰는 ゃ·ゅ·ょ)
}

// 글자 학습 그룹 (GET /api/v1/ig/classroom-character-group 응답)
export interface IClassroomCharacterGroupData extends IBaseData {
  languageCode: EApiLanguageCode;
  characterType: EApiCharacterType;
  subCharacterType: EApiSubCharacterType | null;
  groupKey: string;
  groupOrder: number;
  characters: IClassroomCharacterData[];
}

export interface IClassroomCharacterGroupWorkbookData extends IBaseData {
  studiedAt: string | null;
  isBookmark: boolean | null;
  bookmarkedAt: string | null;
  characterGroup: IClassroomCharacterGroupData;
}

export interface IReadCharacterGroupWorkbooksRequest {
  languageCode: EApiLanguageCode;
  characterType: EApiCharacterType;
  studentKey?: string;
}

export interface IModifyCharacterGroupWorkbookRequest {
  workbookKey: string;
  isStudy?: boolean | null;
  isBookmark?: boolean | null;
}

// 글자 발음 (ko=한글, en=로마자, ja=가나)
export interface ICharacterPronunciation {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

// 단어 뜻 (다국어)
export interface ICharacterMeaning {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

// 예시 단어 1개
export interface ICharacterExample {
  vocabulary: string; // 일본어 단어 (예: 'あめ')
  pronunciation: ICharacterPronunciation; // 단어 발음 (ko=한글읽기, ja=가나)
  audioLink: string | null; // 단어 발음 mp3
  meaning: ICharacterMeaning; // 단어 뜻 (다국어)
}

// 퀴즈 문제 유형
export enum EApiCharacterProblemType {
  READING = 'READING', // 읽기 (글자/단어 보고 발음 고르기)
  LISTENING = 'LISTENING', // 듣기 (발음 듣고 글자/단어 고르기)
}

// 퀴즈 문제 보기 1개 — 의미 고정: ko=한글 발음, en=로마자, ja=가나 표기 (표시 역할은 문제 type에 따라 프론트가 결정)
export interface ICharacterProblemChoice {
  ko: string | null;
  en: string | null;
  ja: string | null;
  correct: boolean;
}

// 퀴즈 문제 1개 — 일반 글자 2문제(읽1+듣1, 보기=행 전체), 촉음/요음 4문제(읽2+듣2, 보기 4개)
export interface ICharacterProblem {
  type: EApiCharacterProblemType;
  question: string; // READING: 화면 표시 텍스트 / LISTENING: TTS 대상 텍스트
  audioLink: string | null; // LISTENING 문제 mp3 (READING은 null)
  choice: ICharacterProblemChoice[];
}

// CharacterGroup에 포함되는 가나 글자 1개
// - 히라가나/가타카나는 소리가 같고 모양만 다른 별도 글자
export interface IClassroomCharacterData extends IBaseData {
  characterOrder: number; // 그룹 안에서의 글자 순서 (0~4)
  symbol: string; // 글자 (예: 'あ' 또는 'ア')
  pronunciation: ICharacterPronunciation; // 글자 발음 (ko=한글, en=로마자, ja=가나)
  audioLink: string | null; // 글자 발음 mp3
  example: ICharacterExample[]; // 예시 단어 목록
  problem: ICharacterProblem[]; // 퀴즈 문제 목록 (백엔드 큐레이션 — 프론트는 셔플·렌더만)
  description: string | null; // 글자 설명
}

// ###################################
// classroom grammar (일본어교실 입문 — 기초문법)
// ###################################
// 백엔드 ClassroomGrammar의 다국어 JSONB 값과 1:1 (각 필드 전용 타입)
export interface IClassroomGrammarStageName {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomGrammarTopicName {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomGrammarName {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomGrammarMeaning {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomGrammarTranslation {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomGrammarRule {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomGrammarNote {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomGrammarVocabulary {
  keyword: string;
  pronunciation: string;
  meaning: IClassroomGrammarMeaning;
}

export interface IClassroomGrammarExample {
  sentence: string;
  pronunciation: string;
  translation: IClassroomGrammarTranslation;
  audioLink: string | null;
  vocabulary: IClassroomGrammarVocabulary[];
}

export interface IClassroomGrammarChoice {
  key: string;
  text: string;
  pronunciation: string | null;
  correct: boolean;
}

export interface IClassroomGrammarProblem {
  key: string;
  question: string;
  pronunciation: string | null;
  translation: IClassroomGrammarTranslation;
  vocabulary: IClassroomGrammarVocabulary[];
  choice: IClassroomGrammarChoice[];
  audioLink: string | null;
}

// 예문 문장(일본어 + 발음 + 번역)
export interface IClassroomGrammarWatchSentence {
  text: string; // 일본어 문장
  pronunciation?: string | null; // 후리가나(선택)
  translation?: string | null; // 한국어 번역
}

export interface IClassroomGrammarWatchExpression {
  text: string;
  pronunciation?: string | null;
  translation?: string | null;
}

export interface IClassroomGrammarWatchContentItem {
  label: string;
  content?: string | null;
  vocabulary?: IClassroomGrammarWatchItemVocabulary[];
}

export interface IClassroomGrammarWatchItemVocabulary {
  keyword: string;
  pronunciation?: string | null;
  meaning?: string | null;
}

export type TClassroomGrammarWatchPhase =
  | 'intro'
  | 'concept'
  | 'rule'
  | 'demonstration'
  | 'example'
  | 'note'
  | 'summary';

export interface IClassroomGrammarWatchConstruction {
  from: string[];
  to: string;
  pronunciation: string;
  translation: string;
}

export interface IClassroomGrammarWatchKeyword {
  label?: string | null;
  content: string;
  pronunciation?: string | null;
}

// 판서 한 장면(scene). at 시점에 등장하며 화면엔 현재 scene 하나만 크게 보인다.
export interface IClassroomGrammarWatchScene {
  at: number; // 등장 시각(ms)
  phase: TClassroomGrammarWatchPhase; // 강의 흐름 단계
  subject?: string | null; // 상단 작은 라벨(예: '명사란?')
  description?: string | null; // subject를 짧게 부연하는 소개 문구
  keyword?: IClassroomGrammarWatchKeyword[] | null; // 큰 제목 목록
  construction?: IClassroomGrammarWatchConstruction[]; // 형태 조립 과정과 완성 표현의 읽기·뜻
  expression?: IClassroomGrammarWatchExpression[]; // 비교해서 보여줄 표현과 읽기·뜻
  sentence?: IClassroomGrammarWatchSentence; // 완결 예문과 읽기·뜻
  item?: IClassroomGrammarWatchContentItem[]; // concept의 개념 항목 또는 summary의 요약 항목
}

// 강의 = 서버 산출물. script(세그먼트)는 서버 내부 합성용이라 응답에 없다.
// 프론트는 audioLink(concat된 오디오) + scene(실측 at 판서)만 사용한다.
export interface IClassroomGrammarWatchContent {
  audioLink: string | null;
  scene: IClassroomGrammarWatchScene[];
}

export interface IClassroomGrammarWatch {
  ko: IClassroomGrammarWatchContent | null;
  en: IClassroomGrammarWatchContent | null;
}

export interface IClassroomGrammarRead {
  notation: string | null;
  meaning: IClassroomGrammarMeaning | null;
  rule: IClassroomGrammarRule;
  note: IClassroomGrammarNote | null;
  example: IClassroomGrammarExample[];
}

export interface IClassroomGrammarPractice {
  problem: IClassroomGrammarProblem[];
}

export interface IClassroomGrammarData extends IBaseData {
  languageCode: EApiLanguageCode;
  stageKey: string;
  stageOrder: number;
  stageName: IClassroomGrammarStageName;
  topicKey: string;
  topicOrder: number;
  topicName: IClassroomGrammarTopicName;
  grammarKey: string;
  grammarOrder: number;
  grammarName: IClassroomGrammarName;
  watch: IClassroomGrammarWatch | null;
  read: IClassroomGrammarRead | null;
  practice: IClassroomGrammarPractice | null;
  publishedAt: string | null;
}

export interface IClassroomGrammarWorkbookData extends IBaseData {
  studiedAt: string | null;
  isBookmark: boolean | null;
  bookmarkedAt: string | null;
  classroomGrammar: IClassroomGrammarData;
}

export interface IReadClassroomGrammarWorkbooksRequest {
  languageCode: EApiLanguageCode;
  studentKey?: string;
}

export interface IModifyClassroomGrammarWorkbookRequest {
  workbookKey: string;
  isStudy?: boolean | null;
  isBookmark?: boolean | null;
}

// ###################################
// basic expression (교실 기본표현)
// ###################################
export interface IClassroomExpressionName {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomExpressionDescription {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomExpressionTranslation {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomExpressionNote {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomExpressionItem {
  notation: string;
  pronunciation: string | null;
  translation: IClassroomExpressionTranslation;
  audioLink: string | null;
}

export interface IClassroomExpressionTable {
  row: number;
  column: number;
  item: IClassroomExpressionItem[];
}

export type TClassroomExpressionWatchPhase = 'intro' | 'expression' | 'note';

export interface IClassroomExpressionScene {
  phase: TClassroomExpressionWatchPhase;
  subject: string | null;
  description: string | null;
  expression: IClassroomExpressionItem[];
  table?: IClassroomExpressionTable | null;
  at: number | null;
}

export interface IClassroomExpressionWatch {
  scene: IClassroomExpressionScene[];
  audioLink: string | null;
}

export interface IClassroomExpressionRead {
  note: IClassroomExpressionNote | null;
  expression: IClassroomExpressionItem[];
}

export interface IClassroomExpressionChoice {
  key: string;
  notation: string;
  pronunciation: string | null;
  correct: boolean;
}

export interface IClassroomExpressionProblem {
  key: string;
  situation: string;
  speaker: IClassroomExpressionSpeaker[];
  answerIdx: number;
  line: IClassroomExpressionLine[];
  choice: IClassroomExpressionChoice[];
  audioLink: string | null;
}

export interface IClassroomExpressionSpeaker {
  id: string;
  name: string;
  gender: string;
}

export interface IClassroomExpressionLine {
  speaker: string;
  text: string;
  pronunciation: string | null;
  translation: IClassroomExpressionTranslation;
  audioLink: string | null;
}

export interface IClassroomExpressionPractice {
  problem: IClassroomExpressionProblem[];
}

export interface IClassroomExpressionData extends IBaseData {
  languageCode: EApiLanguageCode;
  groupKey: string;
  groupOrder: number;
  expressionKey: string;
  expressionOrder: number;
  expressionName: IClassroomExpressionName;
  description: IClassroomExpressionDescription;
  watch: IClassroomExpressionWatch | null;
  read: IClassroomExpressionRead | null;
  practice: IClassroomExpressionPractice | null;
  publishedAt: string | null;
}

export interface IClassroomExpressionWorkbookData extends IBaseData {
  studiedAt: string | null;
  isBookmark: boolean | null;
  bookmarkedAt: string | null;
  basicExpression: IClassroomExpressionData;
}

export interface IReadClassroomExpressionWorkbooksRequest {
  languageCode: EApiLanguageCode;
  studentKey?: string;
}

export interface IModifyClassroomExpressionWorkbookRequest {
  workbookKey: string;
  isStudy?: boolean | null;
  isBookmark?: boolean | null;
}

// ###################################
// practical conversation (일본어교실 생활회화)
// ###################################
export interface IClassroomConversationName {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomConversationDescription {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomConversationLocalizedText {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomConversationNote {
  ko: string | null;
  en: string | null;
  ja: string | null;
}

export interface IClassroomConversationConversationExample {
  sentence: string;
  translation: string;
}

export interface IClassroomConversationConversationKanji {
  character: string;
  meaning: string[];
  onyomi: string[];
  kunyomi: string[];
}

export interface IClassroomConversationConversationNoteItem {
  keyword: string;
  meaning: string[];
  pronunciation: string[];
  usage: string;
  example: IClassroomConversationConversationExample[];
  kanji: IClassroomConversationConversationKanji[] | null;
}

export interface IClassroomConversationConversationNote {
  vocabulary: IClassroomConversationConversationNoteItem[] | null;
  grammar: IClassroomConversationConversationNoteItem[] | null;
}

export interface IClassroomConversationItem {
  notation: string;
  pronunciation: string | null;
  translation: IClassroomConversationLocalizedText;
  explanation: IClassroomConversationLocalizedText | null;
  audioLink: string | null;
}

export interface IClassroomConversationTable {
  row: number;
  column: number;
  item: IClassroomConversationItem[];
}

export type TClassroomConversationWatchPhase = 'intro' | 'expression' | 'note';

export interface IClassroomConversationScene {
  phase: TClassroomConversationWatchPhase;
  subject: string | null;
  description: string | null;
  expression: IClassroomConversationItem[];
  table?: IClassroomConversationTable | null;
  at: number | null;
}

export interface IClassroomConversationWatch {
  scene: IClassroomConversationScene[];
  audioLink: string | null;
}

export interface IClassroomConversationLine {
  speaker: string;
  text: string;
  pronunciation: string | null;
  translation: IClassroomConversationLocalizedText;
  explanation: IClassroomConversationLocalizedText | null;
  audioLink: string | null;
}

export interface IClassroomConversationSpeaker {
  id: string;
  name: string;
  gender: string;
}

export interface IClassroomConversationFlow {
  situation: string;
  speaker: IClassroomConversationSpeaker[];
  line: IClassroomConversationLine[];
}

export interface IClassroomConversationRead {
  note: IClassroomConversationNote | null;
  conversationNote: IClassroomConversationConversationNote | null;
  expression: IClassroomConversationItem[];
  conversation: IClassroomConversationFlow | null;
}

export interface IClassroomConversationChoice {
  key: string;
  notation: string;
  pronunciation: string | null;
  correct: boolean;
}

export interface IClassroomConversationProblem {
  key: string;
  situation: string;
  answerIdx: number;
  speaker: IClassroomConversationSpeaker[];
  line: IClassroomConversationLine[];
  choice: IClassroomConversationChoice[];
}

export interface IClassroomConversationPractice {
  problem: IClassroomConversationProblem[];
}

export interface IClassroomConversationData extends IBaseData {
  languageCode: EApiLanguageCode;
  groupKey: string;
  groupOrder: number;
  conversationKey: string;
  conversationOrder: number;
  conversationName: IClassroomConversationName;
  description: IClassroomConversationDescription;
  watch: IClassroomConversationWatch | null;
  read: IClassroomConversationRead | null;
  practice: IClassroomConversationPractice | null;
  publishedAt: string | null;
}

export interface IClassroomConversationWorkbookData extends IBaseData {
  studiedAt: string | null;
  isBookmark: boolean | null;
  bookmarkedAt: string | null;
  practicalConversation: IClassroomConversationData;
}

export interface IReadClassroomConversationWorkbooksRequest {
  languageCode: EApiLanguageCode;
  studentKey?: string;
}

export interface IModifyClassroomConversationWorkbookRequest {
  workbookKey: string;
  isStudy?: boolean | null;
  isBookmark?: boolean | null;
}

// ############### ai speaking ###############
// stage(일상생활) → topic(카페에서 주문하기) → task(커피 시키기). 실제 학습 단위는 task다.
export interface ISpeakingName {
  ko: string;
  en: string;
  ja: string;
}

export interface ISpeakingTaskData extends IBaseData {
  languageCode: EApiLanguageCode;
  stageKey: string;
  stageOrder: number;
  stageName: ISpeakingName;
  topicKey: string;
  topicOrder: number;
  topicName: ISpeakingName;
  taskKey: string;
  taskOrder: number;
  taskName: ISpeakingName;
  description: ISpeakingName | null;
  context: ISpeakingContext | null;
}

// 대화 설정. 역할과 첫 턴 후보를 담는다.
export interface ISpeakingContext {
  role: { name: string; gender: string } | null;
  openingTurn:
    | {
        text: string;
        pronunciation: string;
        translation: string;
        note: string | null;
        continuationText: string | null;
        continuationPronunciation: string | null;
        continuationTranslation: string | null;
      }[]
    | null;
}

export interface ICreateSpeakingSessionRequest {
  studentKey: string;
  languageCode: string;
  taskKey?: string;
}

export interface ISpeakingTurnData {
  key: string;
  speaker: 'USER' | 'AI';
  // 발화. 학습자 턴은 STT 전사문 그대로다(고쳐 쓰지 않는다).
  text: string | null;
  pronunciation: string | null;
  translation: string | null;
  // 위 text를 고친 문장. 학습자 턴에서 고칠 점이 있을 때만 있다.
  correctionText: string | null;
  correctionPronunciation: string | null;
  correctionTranslation: string | null;
  // 학습자에게 덧붙이는 설명. 학습자 턴은 무엇을 왜 고쳤는지, AI 턴은 배울 표현이다.
  note: string | null;
  // AI의 말에 이어 학습자가 말해볼 수 있는 예시.
  continuationText: string | null;
  continuationPronunciation: string | null;
  continuationTranslation: string | null;
  audioLink: string | null;
  // 발화가 대화 맥락에 맞는 정도(1~5). 낮으면 전사가 어긋난 것으로 보고 말풍선을 지운다.
  checkScore?: number | null;
  // 위 점수를 매긴 근거. 판정을 들여다볼 때 쓰고 화면에 보여 주지 않는다.
  checkComment?: string | null;
  // 이 턴에서 대화가 끝났다면 세션 완료 시각. reply 응답에만 실린다.
  sessionCompletedAt?: string | null;
  // respond 응답의 과제 달성 안내. DB에는 저장하지 않으며 대화를 종료하지 않는다.
  isTaskCompleted?: boolean | null;
}

export interface ISpeakingSessionData {
  key: string;
  createdAt: string;
  languageCode: string;
  taskKey: string | null;
  // 화면 헤더에 쓸 task 정보. 자유 대화는 null이다.
  speakingTask: ISpeakingTaskData | null;
  // 지금까지의 대화를 요약한 맥락. 화면은 쓰지 않고 LLM이 읽는다.
  context: string | null;
  // 대화를 마친 시각. null이면 진행 중이다.
  studiedAt: string | null;
  // 세션을 실제로 종료한 시각. 완료 여부는 이 값을 기준으로 판단한다.
  completedAt: string | null;
  // 백엔드가 관리하는 한 세션의 최대 학습자 발화 수.
  maxUserTurn: number;
  speakingTurns: ISpeakingTurnData[];
}

// presigned 서명에 쓰인 contentType을 그대로 PUT 헤더에 넣어야 S3가 받아준다.
export interface ISpeakingTurnUploadData {
  uploadUrl: string;
  contentType: string;
}

// 서비스별 일자 학습기록. studyTime은 ms이며 구 record API(초)와 구분한다.
export interface IDailyStudyRecordData {
  studyDate: string;
  languageCode: EApiLanguageCode;
  studyType: EApiStudyType;
  studyCount: number;
  studyTime: number;
}

// Worksheet content snapshots contain no source Cloze identity or student records.
export type TWorksheetContent = Pick<IClozeData,
  | 'sourceKeyword'
  | 'sourceKeywordPronunciation'
  | 'sourceKeywordAudioLink'
  | 'keywordMeaningKo'
  | 'keywordMeaningEn'
  | 'keywordMeaningJa'
  | 'sentenceKo'
  | 'sentencePronunciationKo'
  | 'sentenceTemplateKo'
  | 'sentenceAudioLinkKo'
  | 'sentenceEn'
  | 'sentencePronunciationEn'
  | 'sentenceTemplateEn'
  | 'sentenceAudioLinkEn'
  | 'sentenceJa'
  | 'sentencePronunciationJa'
  | 'sentenceTemplateJa'
  | 'sentenceAudioLinkJa'
  | 'choiceKo'
  | 'choiceEn'
  | 'choiceJa'
  | 'choicePronunciationKo'
  | 'choicePronunciationEn'
  | 'choicePronunciationJa'
  | 'answerKo'
  | 'answerEn'
  | 'answerJa'
  | 'answerPronunciationKo'
  | 'answerPronunciationEn'
  | 'answerPronunciationJa'
  | 'answerExplanationKo'
  | 'answerExplanationEn'
  | 'answerExplanationJa'
  | 'addExplanationKo'
  | 'addExplanationEn'
  | 'addExplanationJa'
  | 'sourceConversation'
  | 'conversationKo'
  | 'conversationEn'
  | 'conversationJa'
  | 'conversationAudioLink'
>;

export interface IWorksheetData extends IBaseData {
  languageCode: EApiLanguageCode;
  level: string | null;
  content: TWorksheetContent;
}

export interface IModifyWorksheetWorkbookRequest {
  workbookKey: string;
  studentKey: string;
  isStudy: boolean;
}

export interface IWorksheetWorkbookData extends IBaseData {
  languageCode: EApiLanguageCode;
  publishedAt: string;
  studiedAt: string | null;
  isBookmark: boolean | null;
  bookmarkedAt: string | null;
  worksheet: IWorksheetData;
}

// Local presentation model assembled from the snapshot and its worksheet metadata.
export type TWorksheetStudyContent = TWorksheetContent & Pick<IWorksheetData, 'key' | 'languageCode'>;

export interface IWorksheetWorkbookUploadData {
  uploadUrl: string;
  contentType: string;
}

export interface IWorksheetWorkbookTranscribeData {
  text: string;
  audioLink: string;
}
